import React, { useState, FC, useEffect } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { Row, Col, Button, Alert, Modal, ModalHeader, ModalBody } from "reactstrap";
import FormInput from "../../form-components/InputComponent";
import FormTextArea from "../../form-components/TextAreaComponent";
import Swal from "sweetalert2";
import { Icon } from "../../../components/Component";
import uomApis from "../../../api/master/uomMaster";
import useSequence from "../../../hooks/useSequence";

interface UomMasterProps {
  id?: string | null;
  formAction?: string | null;
  setAction?: () => void;
  toggleModelPopUp: () => void;
  onSuccess?: (data: any, action: string) => void;
  currentRowData?: any;
  setModal?: (modal: { add: boolean; edit: boolean }) => void;
  getUOMs?: () => void;
}

const UomMaster: FC<UomMasterProps> = ({
  id,
  formAction,
  toggleModelPopUp,
  onSuccess,
  currentRowData,
  setModal,
  getUOMs
}) => {
  const { hasSequence } = useSequence(168);
  const [action, setAction] = useState(formAction);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [alertInfo, setAlertInfo] = useState({
    type: "", // 'success' or 'error'
    message: "",
    errors: [],
  });
  const [loading, setLoading] = useState(false);

  const methods = useForm({
    mode: "onChange",
  });

  const {
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = methods;

  useEffect(() => {
    if (!id && !action) {
      reset({
        uom_type: "primary", // Set default value
        uom_name: "",
        uom_short_code: "",
        uom_description: "",
      });
    }
  }, [id, action, reset]);



  const fetchUomById = async () => {
    if (!id) return;
    try {
      setLoading(true);
      const response = await uomApis.fetchUomById(id);
      if (response?.status && response.data) {
        reset({
          uomSequenceId: response.data.uomSequenceId || "",
          uom_type: response.data.uom_type || "primary",
          uom_name: response.data.uom_name || "",
          uom_short_code: response.data.uom_short_code || "",
          uom_description: response.data.uom_description || "",
        });
      } else {
        setAlertInfo({
          type: "error",
          message: response?.message || "Failed to fetch UOM details.",
          errors: [],
        });
      }
    } catch (error: any) {
      setAlertInfo({
        type: "error",
        message: error.response?.data?.message || "Failed to fetch UOM details.",
        errors: [],
      });
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data: any) => {
    setLoading(true);
    try {
      const payload = {
        uomSequenceId: data.uomSequenceId,
        uom_type: data.uom_type,
        uom_name: data.uom_name,
        uom_short_code: data.uom_short_code,
        uom_description: data.uom_description,
      };
      if (!hasSequence) {
        payload.uomSequenceId = data.uomSequenceId;
      }

      let response;
      if (!action && !id) {
        response = await uomApis.addUOM(payload);

      } else if (action === "edit" && id) {
        response = await uomApis.updateUOM(payload, id);
      }

      if (response?.status) {
        Swal.fire(
          action === "edit" ? "Updated!" : "Created!",
          `UOM has been ${action === "edit" ? "updated" : "created"}`,
          "success"
        );
        if (onSuccess) {
          const actionType = action === "edit" ? "edit" : "create";
          const resultData = {
            ...payload,
            id: action === "edit" ? id : (response.data?.id || response.id || Date.now()),
            ...(response.data || {}),
            status: response.data?.status || "Active",
            createdDate: response.data?.createdDate || new Date().toISOString(),
            referenceID: response.data?.referenceID || `UOM-${Date.now()}`
          };
          onSuccess(resultData, actionType);
        }
        if (getUOMs) {
          getUOMs();
        }
        if (setModal) {
          setModal({ add: false, edit: false });
        }

        toggleModelPopUp();
        if (setAction) setAction(null);
      } else {
        const errorMessage = response?.message || "An error occurred";
        const fullErrorMessage = Array.isArray(errorMessage)
          ? errorMessage.join(", ")
          : errorMessage;

        setAlertInfo({
          type: "error",
          message: fullErrorMessage,
          errors: [],
        });
      }
    } catch (error: any) {
      console.error("Error submitting form:", error);
      setAlertInfo({
        type: "error",
        message: error.response?.data?.message || "Failed to process your request. Please try again.",
        errors: [],
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (row: any) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const deleteUOM = await uomApis.removeUOM(row.id);
          if (deleteUOM.status) {
            if (getUOMs) {
              getUOMs();
            }
            Swal.fire("Deleted!", "UOM has been removed.", "success");
            toggleModelPopUp();
          } else {
            Swal.fire({
              icon: "error",
              title: "Error in submission",
              text: "Please contact administrator",
              focusConfirm: false,
            });
          }
        } catch (error: any) {
          console.log(error.message);
        }
      }
    });
  };

  const togglePreviewModal = () => {
    setIsPreviewModalOpen(!isPreviewModalOpen);
  };

  const renderPreviewContent = () => {
    const formData = watch();
    return (
      <div className="preview-checklist">
        <table className="table table-bordered w-full border-collapse">
          <thead>
            <tr className="bg-gray-100">
              <th className="p-2 text-left font-bold text-gray-900">Field</th>
              <th className="p-2 text-left font-bold text-gray-900">Value</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-t">
              <td className="p-2 text-gray-900 font-medium">UOM Type</td>
              <td className="p-2 text-gray-900 font-medium">
                {formData.uom_type ? formData.uom_type.charAt(0).toUpperCase() + formData.uom_type.slice(1) : "N/A"}
              </td>
            </tr>
            <tr className="border-t">
              <td className="p-2 text-gray-900 font-medium">UOM Name</td>
              <td className="p-2 text-gray-900 font-medium">
                {formData.uom_name || "N/A"}
              </td>
            </tr>
            <tr className="border-t">
              <td className="p-2 text-gray-900 font-medium">Short Code</td>
              <td className="p-2 text-gray-900 font-medium">
                {formData.uom_short_code || "N/A"}
              </td>
            </tr>
            <tr className="border-t">
              <td className="p-2 text-gray-900 font-medium">Description</td>
              <td className="p-2 text-gray-900 font-medium">
                {formData.uom_description || "N/A"}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  };

  React.useEffect(() => {
    if (id && (action === "edit" || action === "view")) {
      fetchUomById();
    }
  }, [id, action]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setAlertInfo({ type: "", message: "", errors: [] });
    }, 2500);
    return () => clearTimeout(timeoutId);
  }, [alertInfo]);

  return (
    <>
      <h5 className="title">
        {action === "edit"
          ? "Edit UOM"
          : action === "view"
            ? "View UOM"
            : "Add Unit Of Measure"}
      </h5>
      {alertInfo.message && (
        <div className="mb-3 mt-1">
          <Alert
            color={alertInfo.type === "error" ? "danger" : "success"}
            className="alert-icon"
          >
            <strong>{alertInfo.message}</strong>
            <Icon
              name={
                alertInfo.type === "error" ? "alert-circle" : "check-circle"
              }
            />
            {alertInfo.errors.length > 0 ? (
              <ul>
                {alertInfo.errors.map((err: any, index: any) => (
                  <li key={index}>{err}</li>
                ))}
              </ul>
            ) : null}
          </Alert>
        </div>
      )}
      <div style={{ marginTop: "15px" }}></div>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="row">
            {/* <div className="col-md-12">
              <FormSelect
                name="uom_type"
                label="UOM Type"
                options={uomTypeOptions}
                required={true}
                placeholder="Select UOM Type"
                disabled={action === "view"}
              />
            </div> */}
            <div className="col-md-12">
              {!hasSequence && (
                // <Col md={6}>
                <FormInput
                  name="uomSequenceId"
                  label="UOM ID"
                  required={true}
                  type="text"
                  pattern={/^[a-zA-Z0-9./_-]+$/}
                  maxLength={30}
                  disabled={action === "edit"}
                  placeholder="Enter UOM ID"
                />
                // </Col>
              )}
            </div>
            <div className="col-md-12">
              <FormInput
                name="uom_name"
                label="UOM Name"
                disabled={action === "view"}
                type="text"
                placeholder="Enter UOM Name"
                required={true}
              />
            </div>
          </div>
          <div style={{ marginTop: "15px" }}></div>
          <div className="row">
            <div className="col-md-12">
              <FormInput
                name="uom_short_code"
                label="Short Code"
                disabled={action === "view"}
                type="text"
                placeholder="Enter Short Code"
                required={true}
                maxLength={10}
              />
            </div>
            <div className="col-md-12">
              <FormTextArea
                name="uom_description"
                label="Description"
                disabled={action === "view"}
                placeholder="Enter Description"
                required={true}
              />
            </div>
          </div>

          {action !== "view" ? (
            <Row className="mt-4">
              <Col lg="12">
                <ul className="align-center flex-wrap flex-sm-nowrap gx-4 gy-2">
                  <li>
                    <Button
                      className="btn btn-primary btn-md"
                      type="submit"
                      color="primary"
                      disabled={loading}
                    >
                      {loading ? "Processing..." : action === "edit" ? "Update" : "Submit"}
                    </Button>
                  </li>
                  <li>
                    <a
                      href="#cancel"
                      onClick={(ev) => {
                        ev.preventDefault();
                        toggleModelPopUp();
                      }}
                      className="link link-light"
                    >
                      Cancel
                    </a>
                  </li>
                </ul>
              </Col>
            </Row>
          ) : (
            <Row className="mt-4">
              <Col lg="12">
                <ul className="align-center flex-wrap flex-sm-nowrap gx-4 gy-2">
                  <li>
                    <div
                      className="btn btn-primary btn-md"
                      onClick={() => setAction("edit")}
                    >
                      <Icon name="pen" />
                      <span>Edit</span>
                    </div>
                  </li>
                  <li>
                    <Button
                      color="secondary"
                      onClick={togglePreviewModal}
                      className="btn btn-secondary btn-md"
                    >
                      <Icon name="eye" />
                      <span>Preview</span>
                    </Button>
                  </li>
                  <li>
                    <Button
                      color="danger"
                      onClick={() => currentRowData && handleRemove(currentRowData)}
                      className="btn btn-danger btn-md"
                    >
                      <Icon name="trash" />
                      <span>Delete</span>
                    </Button>
                  </li>
                </ul>
              </Col>
            </Row>
          )}
        </form>
      </FormProvider>

      {/* Preview Modal */}
      <Modal
        isOpen={isPreviewModalOpen}
        toggle={togglePreviewModal}
        size="lg"
        className="font-medium text-gray-900"
      >
        <ModalHeader
          toggle={togglePreviewModal}
          className="text-xl font-bold text-gray-900"
        >
          UOM Details Preview
        </ModalHeader>
        <ModalBody className="text-gray-900">
          {renderPreviewContent()}
          <div className="d-flex justify-content-end mt-4">
            <Button color="primary" onClick={togglePreviewModal}>
              Close
            </Button>
          </div>
        </ModalBody>
      </Modal>
    </>
  );
};

export default UomMaster;